import { CONTACT, ContactChannel } from '@/content/contact'
import { SITE } from '@/content/site'

export interface EditedWithMetadata {
  readonly software: string
  readonly agent: string
}

export const EDITED_WITH = {
  software: `${SITE.name} (${SITE.url})`,
  agent: `${SITE.name} — ${SITE.url} — Instagram @${CONTACT[ContactChannel.Instagram].handle}`,
} as const satisfies EditedWithMetadata

const JPEG_SOI = Uint8Array.of(0xff, 0xd8)
const JPEG_APP1_MARKER = Uint8Array.of(0xff, 0xe1)
const EXIF_IDENTIFIER = asciiBytes('Exif\0\0', 'EXIF identifier')
const XMP_IDENTIFIER = asciiBytes('http://ns.adobe.com/xap/1.0/\0', 'XMP identifier')
const PNG_SIGNATURE = Uint8Array.of(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)
const PNG_IHDR = 'IHDR'
const PNG_IHDR_DATA_LENGTH = 13
const PNG_SOFTWARE_KEYWORD = 'Software'
const PNG_XMP_KEYWORD = 'XML:com.adobe.xmp'
const TIFF_SOFTWARE_TAG = 0x0131
const TIFF_ASCII_TYPE = 2

function asciiBytes(value: string, label: string): Uint8Array {
  const bytes = Uint8Array.from(value, (character) => character.charCodeAt(0))
  if ([...value].some((character) => character.charCodeAt(0) > 0x7f)) {
    throw new Error(`${label} must contain ASCII characters only`)
  }
  return bytes
}

function concatenate(...parts: readonly Uint8Array[]): Uint8Array {
  const result = new Uint8Array(parts.reduce((length, part) => length + part.length, 0))
  let offset = 0
  for (const part of parts) {
    result.set(part, offset)
    offset += part.length
  }
  return result
}

function xmlAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}

export function validateEditedWithMetadata(metadata: EditedWithMetadata = EDITED_WITH): void {
  if (!metadata.software.trim() || !metadata.agent.trim()) {
    throw new Error('Edited-with metadata requires non-empty software and agent values')
  }
  asciiBytes(metadata.software, 'Edited-with Software')
  for (const character of metadata.agent) {
    const code = character.charCodeAt(0)
    if (code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) {
      throw new Error('Edited-with software agent contains an invalid XML control character')
    }
  }
}

function xmpPacket(metadata: EditedWithMetadata): Uint8Array {
  const software = xmlAttribute(metadata.software)
  const agent = xmlAttribute(metadata.agent)
  return new TextEncoder().encode(
    `<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"><rdf:Description rdf:about="" xmlns:xmp="http://ns.adobe.com/xap/1.0/" xmlns:xmpMM="http://ns.adobe.com/xap/1.0/mm/" xmlns:stEvt="http://ns.adobe.com/xap/1.0/sType/ResourceEvent#" xmp:CreatorTool="${software}"><xmpMM:History><rdf:Seq><rdf:li rdf:parseType="Resource" stEvt:action="edited" stEvt:softwareAgent="${agent}"/></rdf:Seq></xmpMM:History></rdf:Description></rdf:RDF></x:xmpmeta>`,
  )
}

function exifPayload(metadata: EditedWithMetadata): Uint8Array {
  const software = concatenate(asciiBytes(metadata.software, 'EXIF Software'), Uint8Array.of(0))
  const valueOffset = 26
  const tiff = new Uint8Array(valueOffset + software.length)
  const view = new DataView(tiff.buffer)
  tiff.set(asciiBytes('II', 'TIFF byte order'), 0)
  view.setUint16(2, 42, true)
  view.setUint32(4, 8, true)
  view.setUint16(8, 1, true)
  view.setUint16(10, TIFF_SOFTWARE_TAG, true)
  view.setUint16(12, TIFF_ASCII_TYPE, true)
  view.setUint32(14, software.length, true)
  view.setUint32(18, valueOffset, true)
  view.setUint32(22, 0, true)
  tiff.set(software, valueOffset)
  return concatenate(EXIF_IDENTIFIER, tiff)
}

function jpegApp1(payload: Uint8Array): Uint8Array {
  const segmentLength = payload.length + 2
  if (segmentLength > 0xffff) throw new Error('Edited-with JPEG APP1 metadata exceeds 65,535 bytes')
  return concatenate(
    JPEG_APP1_MARKER,
    Uint8Array.of(segmentLength >>> 8, segmentLength & 0xff),
    payload,
  )
}

export function injectJpegMetadata(
  bytes: Uint8Array,
  metadata: EditedWithMetadata = EDITED_WITH,
): Uint8Array {
  validateEditedWithMetadata(metadata)
  if (bytes.length < JPEG_SOI.length || bytes[0] !== JPEG_SOI[0] || bytes[1] !== JPEG_SOI[1]) {
    throw new Error('JPEG metadata injection requires an SOI marker')
  }
  return concatenate(
    JPEG_SOI,
    jpegApp1(exifPayload(metadata)),
    jpegApp1(concatenate(XMP_IDENTIFIER, xmpPacket(metadata))),
    bytes.subarray(JPEG_SOI.length),
  )
}

const CRC32_TABLE = Uint32Array.from({ length: 256 }, (_, index) => {
  let value = index
  for (let bit = 0; bit < 8; bit += 1) {
    value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
  }
  return value >>> 0
})

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of bytes) {
    const tableValue = CRC32_TABLE[(crc ^ byte) & 0xff]
    if (tableValue === undefined) throw new Error('PNG CRC32 table is misconfigured')
    crc = tableValue ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = asciiBytes(type, 'PNG chunk type')
  if (typeBytes.length !== 4) throw new Error(`PNG chunk type must contain four characters, received "${type}"`)
  const result = new Uint8Array(12 + data.length)
  const view = new DataView(result.buffer)
  view.setUint32(0, data.length)
  result.set(typeBytes, 4)
  result.set(data, 8)
  view.setUint32(8 + data.length, crc32(result.subarray(4, 8 + data.length)))
  return result
}

function matches(bytes: Uint8Array, expected: Uint8Array, offset = 0): boolean {
  return expected.every((byte, index) => bytes[offset + index] === byte)
}

export function injectPngMetadata(
  bytes: Uint8Array,
  metadata: EditedWithMetadata = EDITED_WITH,
): Uint8Array {
  validateEditedWithMetadata(metadata)
  if (!matches(bytes, PNG_SIGNATURE)) throw new Error('PNG metadata injection requires a valid PNG signature')
  if (bytes.length < 33) throw new Error('PNG metadata injection requires a complete IHDR chunk')
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const ihdrLength = view.getUint32(PNG_SIGNATURE.length)
  const ihdrType = new TextDecoder('ascii').decode(bytes.subarray(12, 16))
  if (ihdrType !== PNG_IHDR || ihdrLength !== PNG_IHDR_DATA_LENGTH) {
    throw new Error('PNG metadata injection requires IHDR as the first 13-byte chunk')
  }
  const ihdrEnd = PNG_SIGNATURE.length + 12 + ihdrLength
  if (ihdrEnd > bytes.length) throw new Error('PNG metadata injection found a truncated IHDR chunk')

  const text = concatenate(
    asciiBytes(PNG_SOFTWARE_KEYWORD, 'PNG Software keyword'),
    Uint8Array.of(0),
    asciiBytes(metadata.software, 'PNG Software'),
  )
  const internationalText = concatenate(
    asciiBytes(PNG_XMP_KEYWORD, 'PNG XMP keyword'),
    Uint8Array.of(0, 0, 0, 0, 0),
    xmpPacket(metadata),
  )
  return concatenate(
    bytes.subarray(0, ihdrEnd),
    pngChunk('tEXt', text),
    pngChunk('iTXt', internationalText),
    bytes.subarray(ihdrEnd),
  )
}

validateEditedWithMetadata()
